import { z } from "zod";
import { isoDateTimeString, pagedResponseSchema, uuidSchema } from "./common";

export const abuseTargetTypeSchema = z.enum([
  "USER",
  "RATING",
  "POSTING",
  "ORGANIZATION",
]);

export const abuseStatusSchema = z.enum([
  "PENDING",
  "UNDER_REVIEW",
  "RESOLVED",
  "REJECTED",
]);

export const abuseReportSchema = z.object({
  id: uuidSchema,
  reporterUserId: uuidSchema,
  reporterRole: z.enum(["WORKER", "EMPLOYER", "ADMIN", "SYSTEM"]),
  targetType: abuseTargetTypeSchema,
  targetId: z.string(),
  reason: z.string(),
  evidenceKey: z.string().nullable().optional(),
  status: abuseStatusSchema,
  assignedAdminId: uuidSchema.nullable().optional(),
  adminNote: z.string().nullable().optional(),
  createdAt: isoDateTimeString,
  resolvedAt: isoDateTimeString.nullable().optional(),
});

export const submitAbuseReportRequestSchema = z.object({
  targetType: abuseTargetTypeSchema,
  targetId: z.string().min(1),
  reason: z.string().min(1).max(2000),
  evidenceKey: z.string().nullable().optional(),
});

// Worker view ("my reports") — cursor-based infinite scroll on mobile.
export const abuseReportListResponseSchema = z.object({
  items: z.array(abuseReportSchema),
  nextCursor: z.string().nullable(),
});

// Admin view — page-number pagination with a total count.
export const adminAbuseReportListResponseSchema =
  pagedResponseSchema(abuseReportSchema);

export const adminResolveAbuseRequestSchema = z.object({
  outcome: z.enum(["RESOLVED", "REJECTED"]),
  note: z.string().max(2000).optional(),
  hideRatingId: uuidSchema.nullable().optional(),
  suspendUserId: uuidSchema.nullable().optional(),
  refundCreditCount: z.number().int().min(0).max(100).optional(),
});

export type AbuseTargetType = z.infer<typeof abuseTargetTypeSchema>;
export type AbuseStatus = z.infer<typeof abuseStatusSchema>;
export type AbuseReport = z.infer<typeof abuseReportSchema>;
export type SubmitAbuseReportRequest = z.infer<
  typeof submitAbuseReportRequestSchema
>;
export type AbuseReportListResponse = z.infer<
  typeof abuseReportListResponseSchema
>;
export type AdminAbuseReportListResponse = z.infer<
  typeof adminAbuseReportListResponseSchema
>;
export type AdminResolveAbuseRequest = z.infer<
  typeof adminResolveAbuseRequestSchema
>;
